import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";
import { fetchSpeakerByIdByUsers } from "../../Api/Users/getUsersApi";
import SocialShare from "./SocialShare";
import axiosInstance from "../../axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import { useAuth } from "../../AuthContext";
import {
  getFollowers,
  getFollowing,
  followUser,
  unfollowUser,
} from "../../Api/followService";

const SpeakerDetail = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";

  const { speakerId } = useParams();
  const { userId } = useAuth();

  const [speaker, setSpeaker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, onChangeHeader] = useState();
  const [favorites, setFavorites] = useState([]);
  const { user, isAuthenticated } = useAuth();
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);

  const isArabic = i18n.language === "ar";

  const toggleFavorite = async (speakerId) => {
    if (isAuthenticated) {
      try {
        const isFavorite = favorites.includes(speakerId);
        const action = isFavorite ? "remove" : "add";

        // إرسال الطلب بناءً على ما إذا كان المفضل موجودًا أم لا
        if (action === "add") {
          await axiosInstance.post(`/api/v1/favorites/speakers/${speakerId}`);
          toast.success(t("favoriteAdded")); // تظهر رسالة عند الإضافة بنجاح
        } else {
          await axiosInstance.delete(`/api/v1/favorites/speakers/${speakerId}`);
          toast.success(t("favoriteRemoved")); // تظهر رسالة عند الحذف بنجاح
        }

        // تحديث قائمة المفضلات بعد الإضافة أو الحذف
        setFavorites((prevFavorites) => {
          if (isFavorite) {
            return prevFavorites.filter((id) => id !== speakerId);
          }
          return [...prevFavorites, speakerId];
        });
      } catch (error) {
        console.error("Error updating favorite:", error);
        toast.error(t("errorUpdatingFavorite"));
      }
    } else {
      toast.warn(t("notAuthenticated"));
    }
  };

  const handleFollowToggle = async (targetUserId) => {
    const isAlreadyFollowing =
      Array.isArray(following) &&
      following.some((user) => user.id === targetUserId);

    try {
      if (isAlreadyFollowing) {
        const success = await unfollowUser(targetUserId);
        if (success) {
          setFollowing(following.filter((user) => user.id !== targetUserId));
          setFollowers(
            followers.map((user) =>
              user.id === targetUserId ? { ...user, isFollowing: false } : user
            )
          );
          toast.success(t("unfollowSuccess"));
        }
      } else {
        const success = await followUser(targetUserId);
        if (success) {
          const updatedFollowing = await getFollowing(userId);
          setFollowing(updatedFollowing.data); // تأكد أن `data` فعلاً مصفوفة
          setFollowers(
            followers.map((user) =>
              user.id === targetUserId ? { ...user, isFollowing: true } : user
            )
          );
          toast.success(t("followSuccess"));
        }
      }
    } catch (error) {
      console.error("Error toggling follow:", error);
      toast.error(t("حدث خطأ أثناء تعديل المتابعة"));
    }
  };

  // حالة لتخزين بيانات المودال
  const [modalData, setModalData] = useState({
    title: "",
    description: "",
    date: "",
    source: "",
    thumbnail: "",
    duration: "",
  });

  // دالة لتحديث بيانات المودال عند النقر على فيديو
  const handleVideoClick = (video) => {
    setModalData({
      title: i18n.language === "ar" ? video.arabicTitle : video.englishTitle,
      description:
        i18n.language === "ar"
          ? video.arabicDescription
          : video.englishDescription,
      date: video.createdOn,
      source: video.link,
      thumbnail: video.thumbnail || "default-thumbnail.jpg",
      duration: video.duration || "00:00",
      videoId: video.videoId, 
    });
  };

  // دالة لتحديد الأيقونة بناءً على النوع
  const getIconByType = (type) => {
    switch (type) {
      case "work":
        return "fa-briefcase";
      case "education":
        return "fa-graduation-cap";
      case "certificate":
        return "fa-certificate";
      case "award":
        return "fa-award";
      default:
        return "fa-certificate"; 
    }
  };

  useEffect(() => {
    AOS.init({ duration: 800 });

    const fetchSpeaker = async () => {
      try {
        const response = await fetchSpeakerByIdByUsers(speakerId);
        setSpeaker(response);
      } catch (err) {
        console.error("Error fetching speaker:", err);
      } finally {
        setLoading(false);
      }
    };

    if (speakerId) {
      fetchSpeaker();
    }
  }, [speakerId]);

  if (loading)
    return <div className="text-center">{t("SpeakerDetail.loading")}</div>;
  if (!speaker) return <div>{t("SpeakerDetail.speaker_not_found")}</div>;

  const {
    profileImage,
    profileCoverImage,
    arabicFullName,
    englishFullName,
    arabicShortBiography,
    englishShortBiography,
    city,
    country,
    languages,
    priceRange,
    mainTopics,
    profileVisitCount,
    followersCount,
    favoritesCount,
    availableInWeeks,
    socialLinks,
  } = speaker;

  const fullName = isArabic ? arabicFullName : englishFullName;
  const shortBio = isArabic ? arabicShortBiography : englishShortBiography;

  const extractYouTubeId = (url) => {
    if (!url) return "";
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : "";
  };

  return (
    <div dir={languageDirection}>
      <style>
        {`
        .booking-option-card {
      border-radius: 12px;
      transition: all 0.3s ease;
      cursor: pointer;
      overflow: hidden;
      border: 2px solid transparent !important;
    }
    
    .booking-option-card:hover {
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
      transform: translateY(-5px);
      border-color: var(--primary-color) !important;
    }
    
    .booking-option-icon {
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .booking-option-footer {
      background-color: rgba(0, 0, 0, 0.02);
    }
    
    .price-range-badge {
      font-size: 0.875rem;
      padding: 0.4rem 0.75rem;
      white-space: nowrap;
      border-radius: 50px;
    }
        `}
      </style>

      {/* Speaker Header Image Section  */}
      <div className="speaker-header-image position-relative">
        <img
          className="speaker-header-bg"
          id="speaker-header-image"
          src={profileCoverImage}
          alt="Cover"
        />
        <div className="speaker-header-overlay"></div>
        <div className="container position-relative"></div>
      </div>

      {/* Speaker Detail Section  */}
      <div id="speaker-detail" className="pt-3" data-aos="fade-up">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm overflow-hidden mb-4">
                <img
                  src={profileImage}
                  alt={fullName}
                  className="card-img-top"
                  style={{ height: "350px", objectFit: "cover" }}
                />
                <div className="card-body">
                  <h3 className="card-title fw-bold text-primary-custom mb-1">
                    {fullName}
                  </h3>
                  <p className="text-muted mb-2">{shortBio}</p>
                  {speaker.showPriceInProfile && speaker.price && (
                    <p className="small mb-3">
                      <span className="badge bg-light text-dark border price-range-badge">
                        <i className="fas fa-tag me-1"></i>
                        {t("SpeakerDetail.price_range_label")}:{" "}
                        <span className="text-primary-custom fw-bold">
                          {speaker.price} {t("sar")}{" "}
                        </span>
                      </span>
                    </p>
                  )}

                  <div className="mb-3">
                    {speaker.city && speaker.country && (
                      <div className="d-flex mb-2 align-items-center">
                        <i className="fas fa-map-marker-alt text-primary-custom ms-3"></i>
                        <p className="mb-0">
                          {isArabic
                            ? `${speaker.city.arabicName}، ${speaker.country.arabicName}`
                            : `${speaker.city.englishName}, ${speaker.country.englishName}`}
                        </p>
                      </div>
                    )}

                    {languages?.length > 0 && (
                      <div className="d-flex mb-2 align-items-center">
                        <i className="fas fa-globe text-primary-custom ms-3"></i>
                        <p className="mb-0">{languages.join("، ")}</p>
                      </div>
                    )}

                    {availableInWeeks && (
                      <div className="d-flex mb-2 align-items-center">
                        <i className="fas fa-calendar-alt text-primary-custom ms-3"></i>
                        <p className="mb-0">
                          {t("SpeakerDetail.available_in")} {availableInWeeks}{" "}
                          {t("weeks")}
                        </p>
                      </div>
                    )}

                    {profileVisitCount && (
                      <div className="d-flex mb-2 align-items-center">
                        <i className="fas fa-eye text-primary-custom ms-3"></i>
                        <p className="mb-0">
                          <span className="view-count">
                            {profileVisitCount.toLocaleString()}
                          </span>{" "}
                          {t("SpeakerDetail.views")}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* زر المفضلة */}
                  <div className="d-flex align-items-center mb-3">
                    <button
                      onClick={() => toggleFavorite(speakerId)}
                      className="btn btn-sm btn-outline-primary me-2 rounded-pill px-3"
                    >
                      <i
                        className={`fas fa-star me-1 ${
                          favorites.includes(speakerId) ? "text-danger" : ""
                        }`}
                      />
                      <span className="favorite-count">
                        {favoritesCount ?? 0}
                      </span>
                    </button>

                    {/* زر المتابعة */}
                    <button
                      className={`btn btn-sm me-2 rounded-pill px-3 ${
                        following?.some((user) => user.id === speakerId)
                          ? "bg-red-100 text-red-600 border border-red-300 hover:bg-red-200"
                          : "bg-blue-100 text-blue-600 border border-blue-300 hover:bg-blue-200"
                      }`}
                      onClick={() => handleFollowToggle(speakerId)}
                    >
                      <i
                        className={`me-1 ${
                          following?.some((user) => user.id === speakerId)
                            ? "fas fa-user-minus"
                            : "fas fa-user-plus"
                        }`}
                      />
                      {following?.some((user) => user.id === speakerId)
                        ? t("SpeakerDetail.unfollow")
                        : t("SpeakerDetail.follow")}
                    </button>

                    {/* فتح المودل */}
                    {/* <button
                      className="btn btn-sm btn-outline-secondary rounded-pill px-2"
                      data-bs-toggle="modal"
                      data-bs-target="#followersModal"
                    >
                      <i className="fas fa-users me-1" />
                      <span className="followers-count">
                        {followersCount ?? 0}
                      </span>
                    </button> */}
                  </div>

                  <div className="card mb-3">
                    <div className="card-body p-3 text-center">
                      <h6 className="mb-2 fw-bold">
                        {t("SpeakerDetail.scan_qr")}
                      </h6>
                      <div className="d-flex justify-content-center">
                        <img
                          src={speaker.qrImageLink}
                          alt="QR Code"
                          style={{
                            maxWidth: "150px",
                            padding: "10px",
                            background: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* <div className="d-grid gap-2">
                    <button className="btn btn-primary-custom py-2">
                      {t("SpeakerDetail.book_speaker")}
                    </button>
                    <a
                      href="/website/contact.html"
                      className="btn btn-outline-primary py-2"
                    >
                      {t("SpeakerDetail.contact_us")}
                    </a>
                  </div> */}
                </div>
              </div>

              {mainTopics?.length > 0 && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <h5 className="fw-bold mb-3">
                      {t("SpeakerDetail.main_topics")}
                    </h5>
                    <div className="d-flex flex-wrap gap-2">
                      {mainTopics.map((topic, index) => (
                        <span key={index} className="topic-badge">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">
                    {t("SpeakerDetail.social_media")}
                  </h5>
                  <div className="d-flex gap-3 mb-3">
                    {speaker.facebookLink && (
                      <a
                        href={speaker.facebookLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm social-share-btn share-facebook"
                      >
                        <i className="fab fa-facebook-f me-1"></i>
                        <span>{t("SpeakerDetail.facebook")}</span>
                      </a>
                    )}
                    {speaker.xLink && (
                      <a
                        href={speaker.xLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm social-share-btn share-twitter"
                      >
                        <i className="fab fa-x-twitter me-1"></i>
                        <span>{t("SpeakerDetail.x_twitter")}</span>
                      </a>
                    )}
                    {speaker.linkedInLink && (
                      <a
                        href={speaker.linkedInLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm social-share-btn share-linkedin"
                      >
                        <i className="fab fa-linkedin-in me-1"></i>
                        <span>{t("SpeakerDetail.linkedin")}</span>
                      </a>
                    )}
                    {speaker.youtubeLink && (
                      <a
                        href={speaker.youtubeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm social-share-btn share-youtube"
                      >
                        <i className="fab fa-youtube me-1"></i>
                        <span>{t("SpeakerDetail.youtube")}</span>
                      </a>
                    )}
                    {speaker.githubLink && (
                      <a
                        href={speaker.githubLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm social-share-btn share-github"
                      >
                        <i className="fab fa-github me-1"></i>
                        <span>{t("SpeakerDetail.github")}</span>
                      </a>
                    )}
                    {speaker.websiteLink && (
                      <a
                        href={speaker.websiteLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm social-share-btn share-website"
                      >
                        <i className="fas fa-globe me-1"></i>
                        <span>{t("SpeakerDetail.website")}</span>
                      </a>
                    )}
                  </div>
                  <h5 className="fw-bold mb-3">
                    {t("SpeakerDetail.share_profile")}
                  </h5>
                  {/* Share Speaker */}
                  <div className="mb-5">
                    {/* <h3 className="h5 fw-bold mb-3" data-i18n="share_speaker">
                      {t("share_speaker")}
                    </h3> */}

                    {(() => {
                      const currentUrl = window.location.href;
                      const encodedUrl = encodeURIComponent(currentUrl);
                      const currentLang = i18n.language;

                      const message = encodeURIComponent(
                        currentLang === "ar"
                          ? `👤 تصفح هذا المتحدث عبر متحدثون:\n${currentUrl}`
                          : `👤 Check out this speaker on Moutakhidoun:\n${currentUrl}`
                      );

                      return (
                        <div className="social-share-buttons d-flex flex-wrap gap-2">
                          <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                            className="social-share-btn share-facebook"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fab fa-facebook-f"></i>{" "}
                            {t("facebook")}
                          </a>
                          <a
                            href={`https://twitter.com/intent/tweet?text=${message}`}
                            className="social-share-btn share-twitter"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i></i> {t("twitter")}
                          </a>
                          <a
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                            className="social-share-btn share-linkedin"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fab fa-linkedin-in"></i>{" "}
                            {t("linkedin")}
                          </a>
                          <a
                            href={`https://api.whatsapp.com/send?text=${message}`}
                            className="social-share-btn share-whatsapp"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fab fa-whatsapp"></i> {t("whatsapp")}
                          </a>
                          <a
                            href={`https://t.me/share/url?url=${encodedUrl}&text=${message}`}
                            className="social-share-btn share-telegram"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fab fa-telegram-plane"></i>{" "}
                            {t("telegram")}
                          </a>
                          <button
                            className="social-share-btn share-copy"
                            onClick={() => {
                              navigator.clipboard.writeText(currentUrl);
                              alert(t("link_copied"));
                            }}
                          >
                            <i className="fas fa-link"></i> {t("copy_link")}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-8">
              {/* قسم النبذة */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <h4 className="fw-bold mb-3"> {t("SpeakerDetail.about")} </h4>
                  <p>
                    {speaker?.arabicCompleteBiography ||
                      speaker?.englishCompleteBiography}
                  </p>
                </div>
              </div>

              {/* قسم الخبرات والمؤهلات */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <h4 className="fw-bold mb-3">
                    {t("SpeakerDetail.speaker.WorkshopContributions")}
                  </h4>
                  <ul className="list-unstyled">
                    {speaker?.speakerContributions?.length > 0 ? (
                      speaker.speakerContributions.map((item, index) => (
                        <li key={item.id || index} className="mb-3">
                          <div className="d-flex">
                            <div className="ms-3 text-primary-custom">
                              <i
                                className={`fas ${getIconByType(
                                  item.type
                                )} fa-2x`}
                              ></i>
                            </div>
                            <div>
                              <h6 className="fw-bold mb-1">
                                {isArabic
                                  ? item.arabicTitle
                                  : item.englishTitle}
                              </h6>
                              <p className="text-muted mb-0">
                                {isArabic
                                  ? item.arabicDescription
                                  : item.englishDescription}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="mb-3">
                        <div className="d-flex">
                          <div className="ms-3 text-primary-custom">
                            <i className="fas fa-graduation-cap fa-2x"></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-1">
                              {isArabic
                                ? speaker?.arabicJobTitle
                                : speaker?.englishJobTitle}
                            </h6>
                            <p className="text-muted mb-0">
                              {isArabic
                                ? speaker?.arabicOrganizationName
                                : speaker?.englishOrganizationName}
                            </p>
                          </div>
                        </div>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
              {/* <!-- Speaker Events --> */}
              {/* {speaker.speakerArticles?.length > 0 && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-4">
                      <h4 className="fw-bold mb-0">
                        {t("SpeakerDetail.my_events")}
                      </h4>
                      <a href="#" className="btn btn-sm btn-link ms-auto">
                        {t("SpeakerDetail.view_all_events")}
                      </a>
                    </div>
                    <div className="row g-4">
                      {speaker.speakerArticles.map((event, index) => (
                        <div className="col-md-6" key={index}>
                          <div className="card event-card h-100 border-0 shadow-sm position-relative">
                            <img
                              src={event.bannerUrl}
                              className="card-img-top"
                              alt={event.title}
                              style={{ height: "140px", objectFit: "cover" }}
                            />
                            <div className="position-absolute top-0 start-0 m-2">
                              <span className="badge bg-info text-white py-2 px-3 rounded-pill">
                                {t("SpeakerDetail.speaker_role")}
                              </span>
                            </div>
                            <div className="position-absolute top-0 end-0 m-2">
                              <span className="badge bg-primary-custom rounded-pill py-2 px-3">
                                {t("SpeakerDetail.in_person")}
                              </span>
                            </div>
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0 fw-bold">{event.title}</h6>
                                <span className="text-primary-custom fw-bold">
                                  {event.date}
                                </span>
                              </div>
                              <p className="mb-0 text-muted small">
                                <i
                                  className={`fas ${
                                    event.isOnline
                                      ? "fa-video"
                                      : "fa-map-marker-alt"
                                  } me-2`}
                                ></i>
                                {event.location ||
                                  t("SpeakerDetail.events_no_location")}
                              </p>
                              <p className="small mb-3 mt-2">
                                {event.shortDescription?.slice(0, 70) || "..."}
                              </p>
                              <div className="d-flex justify-content-end">
                                <a
                                  href={event.detailsLink}
                                  className="btn btn-sm btn-outline-primary px-3"
                                >
                                  {t("SpeakerDetail.details")}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )} */}

              {/* <!-- Books Section --> */}
              {speaker.speakerBooks?.length > 0 && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-4">
                      <h4 className="fw-bold mb-0">
                        {t("SpeakerDetail.speakerBooks")}
                      </h4>
                      {/* <a href="#" className="btn btn-sm btn-link ms-auto">
                        {t("SpeakerDetail.viewAllBooks")}
                      </a> */}
                    </div>
                    <div className="row g-4">
                      {speaker.speakerBooks.map((book) => (
                        <div className="col-md-4 col-sm-6" key={book.id}>
                          <div className="card book-card h-100 border-0 shadow-sm">
                            <div className="book-cover">
                              <img
                                src={book.coverPagePath}
                                className="card-img-top"
                                alt={
                                  i18n.language === "ar"
                                    ? book.arabicTitle
                                    : book.title
                                }
                              />
                            </div>
                            <div className="card-body">
                              <h6 className="card-title fw-bold">
                                {i18n.language === "ar"
                                  ? book.arabicTitle
                                  : book.title}
                              </h6>
                              <p className="card-text small text-muted">
                                {book.createdOn.slice(0, 4)}
                              </p>
                              <p
                                className="card-text small book-description"
                                data-book-id={book.id}
                              >
                                {i18n.language === "ar"
                                  ? book.arabicDescription?.slice(0, 70) + "..."
                                  : book.englishDescription?.slice(0, 70) +
                                    "..."}
                              </p>
                              <div className="d-flex align-items-center mt-2">
                                {/* <button
                                  className="btn btn-sm btn-link p-0 read-more-book"
                                  data-book-id={book.id}
                                >
                                  {t("SpeakerDetail.readMore")}
                                </button> */}
                                <a
                                  href={book.link}
                                  className="btn btn-sm btn-outline-primary ms-auto"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* <!-- Blog Posts Section --> */}
              {speaker.speakerArticles?.length > 0 && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-4">
                      <h4 className="fw-bold mb-0">
                        {t("SpeakerDetail.blog.title")}
                      </h4>
                      {/* <a href="#" className="btn btn-sm btn-link ms-auto">
                        {t("SpeakerDetail.blog.viewAll")}
                      </a> */}
                    </div>
                    <div className="row g-4">
                      {speaker.speakerArticles.map((article) => (
                        <div className="col-lg-6" key={article.id}>
                          <div className="card blog-card border-0 shadow-sm h-100">
                            <div
                              style={{
                                position: "relative",
                                width: "100%",
                                aspectRatio: "16 / 9",
                                overflow: "hidden",
                                borderRadius: "0.5rem",
                              }}
                            >
                              <img
                                src={article.thumbnailImagePath}
                                className="card-img-top"
                                alt={
                                  i18n.language === "ar"
                                    ? article.arabicTitle
                                    : article.englishTitle
                                }
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  objectPosition: "center",
                                }}
                              />
                            </div>
                            <div className="card-body">
                              <div className="d-flex align-items-center mb-2">
                                <span className="badge bg-primary-custom rounded-pill me-2">
                                  {article.articleTypeName}
                                </span>
                                <small className="text-muted">
                                  {new Date(
                                    article.createdOn
                                  ).toLocaleDateString(
                                    i18n.language === "ar" ? "ar-EG" : "en-US",
                                    {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    }
                                  )}
                                </small>
                              </div>
                              <h6 className="card-title fw-bold">
                                {i18n.language === "ar"
                                  ? article.arabicTitle
                                  : article.englishTitle}
                              </h6>
                              <p className="card-text small">
                                {i18n.language === "ar"
                                  ? article.arabicDescription?.slice(0, 70) +
                                    "..."
                                  : article.englishDescription?.slice(0, 70) +
                                    "..."}
                              </p>
                              <div className="d-flex align-items-center mt-3">
                                <div className="d-flex align-items-center">
                                  <i className="far fa-eye me-1 text-muted"></i>
                                  <small className="text-muted">
                                    {article.viewCount}
                                  </small>
                                </div>
                                <a
                                  href={`/blog-detail/${article.id}`}
                                  className="btn btn-sm btn-link ms-auto"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t("SpeakerDetail.blog.readMore")}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* <!-- Videos Section --> */}
              {speaker.speakerVideos?.length > 0 && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-4">
                      <h4 className="fw-bold mb-0">
                        {t("SpeakerDetail.previousVideos")}
                      </h4>
                    </div>
                    <div className="row g-4">
                      {speaker.speakerVideos.map((video) => {
                        if (!video?.link) return null;
                        const videoId = extractYouTubeId(video.link);
                        return (
                          <div className="col-lg-4 col-md-6" key={video.id}>
                            <div
                              className="video-card"
                              data-bs-toggle="modal"
                              data-bs-target="#videoModal"
                              onClick={() =>
                                handleVideoClick({
                                  ...video,
                                  videoId,
                                })
                              }
                            >
                              <div className="ratio ratio-16x9">
                                <iframe
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={video.title}
                                  allowFullScreen
                                ></iframe>
                              </div>
                              <div className="card-body">
                                <h6 className="card-title">{video.title}</h6>
                                <p className="card-text small">
                                  {i18n.language === "ar"
                                    ? video.arabicDescription?.slice(0, 70) +
                                      "..."
                                    : video.englishDescription?.slice(0, 70) +
                                      "..."}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* مودال الفيديو */}
              <div className="ratio ratio-16x9 mb-3">
                {modalData.videoId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${modalData.videoId}?autoplay=1`}
                    title={modalData.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <p className="text-muted">
                    {t("SpeakerDetail.videoUnavailable")}
                  </p>
                )}
              </div>

              {/* النهاية */}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SpeakerDetail;
